import { toast } from 'sonner';

// ============ Config ============
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'hsq1yg-gg.myshopify.com';
const SHOPIFY_STOREFRONT_TOKEN = '5e002fc283b9c3ea4cf9723f559249ef';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;

// Metafields we read from Shopify (namespace "riham")
export const RIHAM_METAFIELD_KEYS = [
  'subtitle',
  'tagline',
  'size',
  'long_description', // multi-paragraph; separated by blank lines
  'ingredients',
  'usage', // canonical "how to use" key in Shopify
  'how_to_use', // legacy mirror written by the admin editor
  'benefits', // JSON array of strings
  'faqs', // JSON array of { q, a } — Shopify metafield key is `faqs` (plural)
  'video_url',
  // Service-specific
  'duration',
  'suitable_for',
  'before_treatment',
  'after_treatment',
] as const;

// ============ Core fetch helper ============
export async function storefrontApiRequest<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T | null> {
  const res = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (res.status === 402) {
    toast.error('Shopify: payment required', {
      description: 'Shopify API access requires an active billing plan.',
    });
    return null;
  }
  if (!res.ok) throw new Error(`Shopify API error ${res.status}`);
  const data = await res.json();
  if (data.errors) throw new Error(`Shopify: ${data.errors.map((e: any) => e.message).join(', ')}`);
  return data as T;
}

// ============ Product types & query ============
export interface ShopifyMetafieldReference {
  node: {
    id: string;
    handle: string;
    type: string;
    fields: Array<{ key: string; value: string }>;
  };
}

export interface ShopifyMetafield {
  key: string;
  namespace: string;
  value: string;
  type: string;
  references?: {
    edges: ShopifyMetafieldReference[];
  };
}

export interface ShopifyProductNode {
  id: string;
  title: string;
  description: string;
  handle: string;
  tags: string[];
  productType: string;
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  compareAtPriceRange?: {
    minVariantPrice: { amount: string; currencyCode: string };
  };
  images: { edges: Array<{ node: { url: string; altText: string | null } }> };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        sku: string;
        availableForSale: boolean;
        price: { amount: string; currencyCode: string };
        compareAtPrice?: { amount: string; currencyCode: string } | null;
        selectedOptions: Array<{ name: string; value: string }>;
      };
    }>;
  };
  metafields?: Array<ShopifyMetafield | null>;
}

const METAFIELD_IDENTIFIERS = RIHAM_METAFIELD_KEYS.map(
  (k) => `{ namespace: "riham", key: "${k}" }`,
).join(', ');

const PRODUCTS_QUERY = `
  query Products($first: Int!) {
    products(first: $first) {
      edges { node {
        id title description handle tags productType
        priceRange { minVariantPrice { amount currencyCode } }
        compareAtPriceRange { minVariantPrice { amount currencyCode } }
        images(first: 10) { edges { node { url altText } } }
        variants(first: 10) { edges { node {
          id title sku availableForSale
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          selectedOptions { name value }
        } } }
        metafields(identifiers: [${METAFIELD_IDENTIFIERS}]) {
          key namespace value type
          references(first: 10) {
            edges {
              node {
                ... on Metaobject {
                  id
                  handle
                  type
                  fields { key value }
                }
              }
            }
          }
        }
      } }
    }
  }
`;

export async function fetchShopifyProducts(): Promise<ShopifyProductNode[]> {
  const data = await storefrontApiRequest<{
    data: { products: { edges: Array<{ node: ShopifyProductNode }> } };
  }>(PRODUCTS_QUERY, { first: 50 });
  if (!data) return [];
  return data.data.products.edges.map((e) => e.node);
}

// Convert metafields[] into a lookup by key (skips nulls).
export function metafieldMap(p: ShopifyProductNode): Record<string, string> {
  const out: Record<string, string> = {};
  (p.metafields ?? []).forEach((m) => {
    if (m && m.value != null) out[m.key] = m.value;
  });
  return out;
}

// Extract metaobject GIDs from a faqs metafield value
export function extractFaqGids(metafield: ShopifyMetafield | null | undefined): string[] {
  if (!metafield?.value) return [];
  if (
    metafield.type !== 'list.metaobject_reference' &&
    !metafield.value.startsWith('["gid://shopify/Metaobject/')
  ) {
    return [];
  }
  try {
    const parsed = JSON.parse(metafield.value);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (x): x is string => typeof x === 'string' && x.startsWith('gid://shopify/Metaobject/'),
      );
    }
  } catch {
    // ignore
  }
  return [];
}

// Resolve FAQ items from a metafield. Supports:
// 1) Old JSON format: [{ q, a }, ...]
// 2) Metaobject list: ["gid://shopify/Metaobject/...", ...]
//    - first via inline `references` (when Storefront token has the scope)
//    - else via the provided `faqResolverMap` (GID -> {question, answer})
export function resolveFaqsFromMetafield(
  metafield: ShopifyMetafield | null | undefined,
  fallback: { q: string; a: string }[],
  faqResolverMap?: Map<string, { question: string; answer: string }>,
): { q: string; a: string }[] {
  if (!metafield?.value) return fallback;

  // Old JSON format
  try {
    const parsed = JSON.parse(metafield.value);
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0] &&
      typeof parsed[0] === 'object' &&
      ('q' in parsed[0] || 'a' in parsed[0])
    ) {
      return parsed
        .map((item: any) => ({ q: item.q ?? '', a: item.a ?? '' }))
        .filter((f: any) => f.q.trim() || f.a.trim());
    }
  } catch {
    // not old JSON
  }

  // Metaobject list
  if (
    metafield.type === 'list.metaobject_reference' ||
    metafield.value.startsWith('["gid://shopify/Metaobject/')
  ) {
    // Inline references (if the storefront token carries the scope)
    const refs = metafield.references?.edges ?? [];
    if (refs.length > 0) {
      const out = refs
        .map((edge) => {
          const fields = edge.node.fields ?? [];
          const get = (key: string) => fields.find((f) => f.key === key)?.value ?? '';
          return {
            q: get('question') || get('q') || get('title'),
            a: get('answer') || get('a') || get('body') || get('content'),
          };
        })
        .filter((f) => f.q.trim() || f.a.trim());
      if (out.length) return out;
    }

    // Resolver map (populated from server-side admin call)
    const gids = extractFaqGids(metafield);
    if (gids.length && faqResolverMap && faqResolverMap.size) {
      const out = gids
        .map((gid) => faqResolverMap.get(gid))
        .filter((x): x is { question: string; answer: string } => !!x)
        .map((x) => ({ q: x.question, a: x.answer }))
        .filter((f) => f.q.trim() || f.a.trim());
      if (out.length) return out;
    }

    return fallback;
  }

  return fallback;
}


// ============ Cart mutations ============
const CART_QUERY = `query cart($id: ID!) { cart(id: $id) { id totalQuantity checkoutUrl } }`;

const CART_CREATE = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_ADD = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_DISCOUNT_CODES_UPDATE = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes { code applicable }
      }
      userErrors { field message }
    }
  }
`;

function formatCheckoutUrl(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('channel', 'online_store');
    return u.toString();
  } catch {
    return url;
  }
}

const isCartNotFound = (errs: Array<{ message: string }>) =>
  errs.some((e) => /cart not found|does not exist/i.test(e.message));

export type LineAttribute = { key: string; value: string };

export async function createCart(
  variantId: string,
  quantity: number,
  attributes?: LineAttribute[],
) {
  const line: any = { merchandiseId: variantId, quantity };
  if (attributes && attributes.length) line.attributes = attributes;
  const data = await storefrontApiRequest<any>(CART_CREATE, {
    input: { lines: [line] },
  });
  const errs = data?.data?.cartCreate?.userErrors ?? [];
  if (errs.length) {
    console.error('cartCreate errors', errs);
    return null;
  }
  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;
  const lineId = cart.lines.edges[0]?.node?.id as string | undefined;
  return {
    cartId: cart.id as string,
    checkoutUrl: formatCheckoutUrl(cart.checkoutUrl),
    lineId,
  };
}

export async function addCartLine(
  cartId: string,
  variantId: string,
  quantity: number,
  attributes?: LineAttribute[],
) {
  const line: any = { merchandiseId: variantId, quantity };
  if (attributes && attributes.length) line.attributes = attributes;
  const data = await storefrontApiRequest<any>(CART_LINES_ADD, {
    cartId,
    lines: [line],
  });
  const errs = data?.data?.cartLinesAdd?.userErrors ?? [];
  if (isCartNotFound(errs)) return { cartNotFound: true as const };
  if (errs.length) {
    console.error('cartLinesAdd errors', errs);
    return { success: false as const };
  }
  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges ?? [];
  const line2 = lines.find((l: any) => l.node.merchandise.id === variantId);
  return { success: true as const, lineId: line2?.node?.id as string | undefined };
}

export async function updateCartLine(cartId: string, lineId: string, quantity: number) {
  const data = await storefrontApiRequest<any>(CART_LINES_UPDATE, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });
  const errs = data?.data?.cartLinesUpdate?.userErrors ?? [];
  if (isCartNotFound(errs)) return { cartNotFound: true as const };
  return { success: errs.length === 0 };
}

export async function removeCartLine(cartId: string, lineId: string) {
  const data = await storefrontApiRequest<any>(CART_LINES_REMOVE, {
    cartId,
    lineIds: [lineId],
  });
  const errs = data?.data?.cartLinesRemove?.userErrors ?? [];
  if (isCartNotFound(errs)) return { cartNotFound: true as const };
  return { success: errs.length === 0 };
}

/**
 * Replace the discount codes applied to the cart with the given set.
 * Pass an empty array to clear all codes.
 * Shopify applies the discount at checkout — the cart total in Storefront
 * may not reflect it, so we still compute display totals locally.
 */
export async function updateCartDiscountCodes(cartId: string, codes: string[]) {
  const unique = Array.from(new Set(codes.filter(Boolean)));
  const data = await storefrontApiRequest<any>(CART_DISCOUNT_CODES_UPDATE, {
    cartId,
    discountCodes: unique,
  });
  const errs = data?.data?.cartDiscountCodesUpdate?.userErrors ?? [];
  if (isCartNotFound(errs)) return { cartNotFound: true as const };
  if (errs.length) {
    console.error('cartDiscountCodesUpdate errors', errs);
    return { success: false as const, applied: [] as { code: string; applicable: boolean }[] };
  }
  const applied =
    data?.data?.cartDiscountCodesUpdate?.cart?.discountCodes ??
    ([] as { code: string; applicable: boolean }[]);
  const notApplicable = applied.filter((a: any) => !a.applicable);
  if (notApplicable.length) {
    console.warn('Some bundle discount codes were not applicable:', notApplicable);
  }
  return { success: true as const, applied };
}

export async function getCart(cartId: string) {
  const data = await storefrontApiRequest<any>(CART_QUERY, { id: cartId });
  return data?.data?.cart ?? null;
}

// ============ Variant registry (SKU -> variant info) ============
export interface VariantInfo {
  variantId: string;
  price: number;
  currencyCode: string;
  image?: string;
  availableForSale: boolean;
}

const variantRegistry = new Map<string, VariantInfo>();

export function registerVariant(sku: string, info: VariantInfo): void {
  variantRegistry.set(sku, info);
}

export function getVariantBySku(sku: string): VariantInfo | undefined {
  return variantRegistry.get(sku);
}
